<template>
  <div class="table-wrapper def-table" :class="{ 'table-responsive': responsive }">
    <table
      class="table"
      :class="[{ 'table-striped': striped, 'table-hover': hover, 'table-sm': small, 'has-row-click': hasRowClickHandler }, tbodyClass]"
      :aria-label="ariaLabel"
    >
      <!-- region Table Header -->
      <thead>
        <tr>
          <!--
          @ Accessibility
          -->
          <th
            v-for="(field, fIndex) in normalizedFields"
            :key="'th-' + field.key"
            scope="col"
            :aria-sort="ariaSort(field)"
          >
            <component :is="field.sortable ? 'UnstyledButton' : 'div'"
                       :action="()=>toggleSort(field)"
                       :class="['th-content', 'table-sort-trigger', field.thClass || field.class, { sortable: field.sortable }]"
                       :aria-label="field.sortable ? sortButtonAriaLabel(field) : null"
            >
              <!-- Head slot support: head(fieldKey) -->
              <template v-if="$slots['head(' + field.key + ')']">
                <slot :name="'head(' + field.key + ')'" :field="field" :index="fIndex" :sort-by="sortBy" :sort-desc="sortDesc" />
              </template>
              <b v-else>{{ field.label || field.key }}</b>

              <span v-if="field.sortable" class="table-sort-indicator" aria-hidden="true">
                <span v-if="sortBy === field.key">
                  <span v-if="!sortDesc">▲</span>
                  <span v-else>▼</span>
                </span>
                <span v-else class="sort-indicator-placeholder">⇅</span>
              </span>

            </component>
          </th>
        </tr>
      </thead>
      <!-- endregion Table Header -->
      <!-- region Table Body (FILLED) -->
      <!-- if (sortedItems.length) -->
      <tbody v-if="sortedItems.length">
      <tr
        v-for="(item, index) in sortedItems"
        :key="rowKey(item, index)"
        class="table-row"
        :class="{ 'table-row-interactive': hasRowClickHandler }"
        @click="onRowClick(item)"
        v-bind="rowAttrs(item, index)"
      >
        <!-- todo PR - Question: is the {data-label} attribute used? if not, why not removeing it ? -->
        <td v-for="field in normalizedFields" :key="'td-' + field.key + '-' + index" :data-label="field.label || field.key" :class="field.tdClass || field.class">
          <!-- Cell slot support: cell(fieldKey) ; Default : -->
          <template v-if="field.key === 'actions' && !($slots['cell(actions)'])">
            <div class="table-actions">
              <Button
                preset-size
                v-for="(action, i) in visibleActions(item, index)"
                :key="'action-btn-' + i"
                :class="['action-btn', action.class]"
                :aria-label="getActionAriaLabel(item,action,currentLocale) || getActionTooltip(item,action,currentLocale)"
                :title="getActionTooltip(item,action,currentLocale)"
                :disabled="action.disabled && action.disabled(item, index)"
                @click.stop="onActionClick(action, item, i)"
              >
                <font-awesome-icon v-if="action.icon" :icon="action.icon" />
                <span v-else>{{ action.label }}</span>
              </Button>
            </div>
          </template>
          <template v-else-if="$slots['cell(' + field.key + ')']">
            <slot :name="'cell(' + field.key + ')'" :item="item" :field="field" :value="item[field.key]" :index="index" />
          </template>
          <template v-else>
            <span>{{ valueForCell(item, field, index) }}</span>
          </template>
        </td>
      </tr>
      </tbody>
      <!-- endregion Table Body (FILLED) -->
      <!-- / if (sortedItems.length) ; else -->
      <!-- region Table Body (Empty) -->
      <tbody v-else>
        <tr class="table-empty-row">
          <td :colspan="normalizedFields.length">
            <div class="text-center w-100 py-4">
              <slot name="empty">
                {{ $t(noDataText) }}
              </slot>
            </div>
          </td>
        </tr>
      </tbody>
      <!-- endregion Table Body (Empty) -->
      <tfoot v-if="$slots['custom-foot']">
        <slot name="custom-foot" :items="sortedItems" :sort-by="sortBy" :sort-desc="sortDesc" />
      </tfoot>
    </table>
  </div>
</template>

<script>
import UnstyledButton from "@/components/actions/UnstyledButton.vue";
import Button from "@/components/actions/Button.vue";

export default {
  name: 'Table',
  components: {Button, UnstyledButton},
  inheritAttrs: false,
  props: {
    items: { type: Array, default: () => [] },
    /** @type {Array<String|{key:string,label:string,sortable:boolean}>} */
    fields: { type: Array, default: () => [] },
    responsive: { type: [Boolean, String], default: true },
    striped: { type: Boolean, default: false },
    hover: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    tbodyClass: { type: [String, Array, Object], default: '' },
    labelSortAsc: { type: [Function,String], default: (function($t){return $t('system.ariaLabel.sortAsc');}) },
    labelSortDesc: { type: [Function,String], default: (function($t){return $t('system.ariaLabel.sortDesc');}) },
    labelSortClear: { type: [Function,String], default: (function($t){return $t('system.ariaLabel.sortClear');}) },
    /** @type {{[attribute:String]:String}|function(this:undefined,item:any,index:number)} */
    tbodyTrAttr: { type: [Function, Object], default: null },
    /** Action descriptors for the optional actions column. */
    actions: { type: Array, default: () => [] },
    noDataText: { type: String, default: 'system.label.noData' },
    ariaLabel: String,
  },
  data() {
    return {
      sortBy: null,
      sortDesc: false
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n?.locale || null;
    },
    hasRowClickHandler() {
      return !!(this.$listeners && this.$listeners['row-clicked']);
    },
    normalizedFields() {
      // Normalize fields: accept array of strings or objects like bootstrap-vue
      return (this.fields || []).map(f => {
        if (typeof f === 'string') {
          return { key: f, label: f, sortable: false };
        }
        return {
          key: f.key || f.field || f.label, // attempt multiple fallbacks
          label: f.label || f.key || f.field,
          sortable: !!f.sortable,
          ...f
        };
      });
    },
    sortedItems() {
      const items = [...(this.items || [])];
      if (this.sortBy) {
        const fieldObj = this.normalizedFields.find(f => f.key === this.sortBy);
        if (fieldObj) {
          items.sort((a, b) => {
            const va = this.getSortValue(a, fieldObj);
            const vb = this.getSortValue(b, fieldObj);
            if (va == null && vb == null) return 0;
            if (va == null) return this.sortDesc ? 1 : -1;
            if (vb == null) return this.sortDesc ? -1 : 1;
            // numeric compare first when both look numeric
            const bothNumeric = !isNaN(parseFloat(va)) && !isNaN(parseFloat(vb));
            if (bothNumeric) {
              const nva = parseFloat(va);
              const nvb = parseFloat(vb);
              return this.sortDesc ? nvb - nva : nva - nvb;
            }
            const sa = String(va).toLowerCase();
            const sb = String(vb).toLowerCase();
            if (sa < sb) return this.sortDesc ? 1 : -1;
            if (sa > sb) return this.sortDesc ? -1 : 1;
            return 0;
          });
        }
      }
      return items;
    }
  },
  methods: {
    toggleSort(field) {
      if (!field.sortable) return;
      if (this.sortBy !== field.key) {
        this.sortBy = field.key;
        this.sortDesc = false;
      } else if (!this.sortDesc) {
        this.sortDesc = true;
      } else {
        // clear
        this.sortBy = null;
        this.sortDesc = false;
      }
      this.$emit('sort-changed', { sortBy: this.sortBy, sortDesc: this.sortDesc });
    },
    ariaSort(field) {
      if (!field.sortable) return null;
      if (this.sortBy !== field.key) return 'none';
      return this.sortDesc ? 'descending' : 'ascending';
    },
    sortAriaLabel(field, lang = this.currentLocale) {
      const title = this.sortTitle(field, lang);
      return typeof title === 'function' ? title(this.$t.bind(this), lang) : title;
    },
    sortButtonAriaLabel(field) {
      return `${field.label || field.key}, ${this.sortAriaLabel(field)}`;
    },
    getActionTooltip:function(item,action,lang){
      return typeof action.tooltip === "function" ? action.tooltip(item,lang):action.tooltip;
    },
    getActionAriaLabel:function(item,action,lang){
      return typeof action.ariaLabel === "function" ? action.ariaLabel(item,lang):action.ariaLabel;
    },
    sortTitle(field) {
      if (this.sortBy !== field.key) return this.labelSortAsc;
      if (!this.sortDesc) return this.labelSortDesc;
      return this.labelSortClear;
    },
    onRowClick(item) {
      if (!this.hasRowClickHandler) {
        return;
      }
      this.$emit('row-clicked', item);
    },
    rowKey(item, index) {
      // attempt to use unique id field if present
      return item && (item.id || item._id || item.uuid) ? (item.id || item._id || item.uuid) : index;
    },
    rowAttrs(item, index) {
      if (!this.tbodyTrAttr) return {};
      if (typeof this.tbodyTrAttr === 'function') {
        return this.tbodyTrAttr(item, index) || {};
      }
      return this.tbodyTrAttr;
    },
    formatCell(val) {
      if (val == null) return '';
      if (typeof val === 'boolean') return val ? '✓' : '✕';
      return val;
    },
    visibleActions(item, index) {
      return (this.actions || []).filter(a => typeof a.show === 'function' ? a.show(item, index): typeof a.show ==='boolean' ? a.show : true);
    },
    valueForCell(item, field, index) {
      const raw = item[field.key];
      if (field && field.formatter) {
        let formatter = field.formatter;
        try {
          if (typeof formatter === 'string' && typeof this[formatter] === 'function') {
            return this[formatter](raw, field.key, item, index);
          }
          if (typeof formatter === 'function') {
            return formatter(raw, field.key, item, index);
          }
        } catch (e) {
          // Silently fall back if formatter throws
          return this.formatCell(raw);
        }
      }
      return this.formatCell(raw);
    },
    getSortValue(item, field) {
      // 1. Explicit sortValue function override
      if (field && typeof field.sortValue === 'function') {
        try { return field.sortValue(item, field); } catch (e) { /* ignore */ }
      }
      // 2. Use raw value if present
      const raw = item[field.key];
      if (raw != null) return raw;
      // 3. If raw missing but formatter exists, use formatted display
      if (field && field.formatter) {
        try {
          if (typeof field.formatter === 'function') return field.formatter(raw, field.key, item);
          if (typeof field.formatter === 'string' && typeof this[field.formatter] === 'function') {
            return this[field.formatter](raw, field.key, item);
          }
        } catch (e) { /* ignore */ }
      }
      return raw;
    },
    onActionClick(action, item, index) {
      try {
        if (action && typeof action.handler === 'function') {
          action.handler(item, index, action);
          return;
        }
      } catch (e) {
        console.error('Table action handler error', e);
      }

      if (action && action.event) {
        this.$emit(action.event, item);
      }
    }
  }
};
</script>

